// Plays what a caller hears at one point of the menu. An uploaded recording
// streams from the audio store (short-lived URL fetched on first play); a
// text-to-speech prompt is read by the browser's own voice as an approximation.

import { useEffect, useRef, useState } from 'react';
import { Play, Square, Volume2, FileAudio } from 'lucide-react';
import { api, type Prompt } from '../../api/client';
import { useGuide } from './context';

function ttsLang(code?: string, fallback?: string): string {
  if (code && /^[a-z]{2}(-[A-Z]{2})?$/i.test(code)) return code;
  return fallback === 'nl' ? 'nl-NL' : 'en-US';
}

export function AudioPlayer({ prompt, label, voiceLang }: { prompt?: Prompt; label: string; voiceLang?: string }) {
  const { t, lang, isAdmin } = useGuide();
  const [speaking, setSpeaking] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [srcError, setSrcError] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const expiresRef = useRef(0);

  useEffect(() => () => { if (speaking) window.speechSynthesis?.cancel(); }, [speaking]);

  if (!prompt || prompt.mode === 'none') return null;
  // An audio prompt whose recording was never uploaded has nothing to play. It
  // is worth flagging to an admin, who can go and upload it, but for a reader
  // of the guide it is an empty card — so show it only when there is a
  // transcript to read or an admin to act on it.
  if (prompt.mode === 'audio' && !prompt.audioId && !prompt.text && !isAdmin) return null;

  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;
  function speak() {
    if (!canSpeak || !prompt?.text) return;
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const u = new SpeechSynthesisUtterance(prompt.text);
    u.lang = ttsLang(voiceLang, lang);
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  }

  async function ensureSrc(): Promise<void> {
    if (!prompt?.audioId) return;
    if (src && Date.now() < expiresRef.current - 30_000) return;
    try {
      const { url, expiresAt } = await api.getCallFlowAudioUrl(prompt.audioId);
      expiresRef.current = new Date(expiresAt).getTime();
      setSrc(url); setSrcError(false);
    } catch { setSrcError(true); }
  }

  const isAudio = prompt.mode === 'audio';
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2">
      <div className="flex items-center gap-2 text-xs text-gray-600">
        {isAudio ? <FileAudio className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <Volume2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
        <span className="font-medium text-gray-800">{label}</span>
        <span className="text-gray-400">· {isAudio ? (prompt.teamsFileName || t('audioFile')) : t('textToSpeech')}</span>
      </div>
      {isAudio && prompt.audioId && (
        <div className="print:hidden">
          <audio ref={audioRef} controls preload="none" className="w-full h-8" src={src || undefined}
            onPlay={() => { if (!src) { void ensureSrc().then(() => { const a = audioRef.current; if (a) { a.load(); void a.play().catch(() => {}); } }); } }}
            onError={() => { if (src) { setSrc(null); void ensureSrc(); } }} />
          {srcError && <div className="text-[11px] text-red-600 mt-1">{t('membersFailed')}</div>}
        </div>
      )}
      {isAudio && !prompt.audioId && isAdmin && (
        <div className="text-[11px] text-amber-700">{t('audioNotUploaded')}</div>
      )}
      {prompt.text && (
        <div className="flex items-start gap-2">
          <p className="text-sm text-gray-800 italic flex-1">“{prompt.text}”</p>
          {!isAudio && (
            <button type="button" onClick={speak} disabled={!canSpeak} title={canSpeak ? t('ttsListen') : t('ttsUnavailable')}
              className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 print:hidden">
              {speaking ? <Square className="w-3 h-3" /> : <Play className="w-3 h-3" />}{speaking ? t('audioStop') : t('ttsListen')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
